import React from "react";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import Dialog from "@mui/material/Dialog";
import Popover from "@mui/material/Popover";

import type {
  ContentTopic,
  ContentUploadWindow,
} from "../../@types/portalAluno";
import { UPLOAD_WINDOW_OPTIONS } from "../../utils/portalAluno/uploadWindow";
import useDragScroll from "../ContentCardList/useDragScroll";
import "./styles.css";

const ALL_TOPICS_LABEL = "Todos os temas";

export const EMPTY_TOPICS: ContentTopic[] = [];

export const EMPTY_TAGS: string[] = [];

interface ContentTopicsProps {
  topics: ContentTopic[];
  selectedTopicId: number | null;
  onTopicChange: (topicId: number | null) => void;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  selectedUploadWindow?: ContentUploadWindow | null;
  onUploadWindowChange?: (uploadWindow: ContentUploadWindow | null) => void;
  tags?: string[];
  selectedTag?: string | null;
  onTagChange?: (tag: string | null) => void;
}

const ContentTopics = ({
  topics,
  selectedTopicId,
  onTopicChange,
  isLoading = false,
  isError = false,
  onRetry,
  selectedUploadWindow = null,
  onUploadWindowChange,
  tags,
  selectedTag = null,
  onTagChange,
}: ContentTopicsProps) => {
  const [isTopicsDialogOpen, setIsTopicsDialogOpen] = React.useState(false);
  const [filterAnchor, setFilterAnchor] = React.useState<HTMLElement | null>(
    null,
  );
  const { ref: trackRef, isDragging, dragProps } = useDragScroll(true);
  const selectedTopic = topics.find((topic) => topic.id === selectedTopicId);
  const showUploadFilter = Boolean(onUploadWindowChange);
  const availableTags = tags ?? EMPTY_TAGS;
  const showTagFilter = Boolean(onTagChange) && availableTags.length > 0;
  const isFilterOpen = Boolean(filterAnchor);

  const selectTopic = (topicId: number | null) => {
    onTopicChange(topicId);
    setIsTopicsDialogOpen(false);
  };

  const toggleTopic = (topicId: number) => {
    onTopicChange(selectedTopicId === topicId ? null : topicId);
  };

  const selectUploadWindow = (uploadWindow: ContentUploadWindow | null) => {
    onUploadWindowChange?.(uploadWindow);
    setFilterAnchor(null);
  };

  const selectTag = (tag: string | null) => {
    onTagChange?.(tag);
    setFilterAnchor(null);
  };

  if (isLoading) {
    return (
      <section className="content-topics" aria-label="Carregando temas">
        <div className="content-topics__header">
          <div className="content-topics__heading">
            <h1>Temas</h1>
          </div>
        </div>
        <div className="content-topics__track" aria-busy="true">
          {[0, 1, 2, 3, 4].map((placeholder) => (
            <span className="content-topics__skeleton" key={placeholder} />
          ))}
        </div>
      </section>
    );
  }

  if (isError) {
    return (
      <section className="content-topics" aria-label="Temas">
        <div className="content-topics__header">
          <div className="content-topics__heading">
            <h1>Temas</h1>
          </div>
        </div>
        <div className="content-topics__message" role="alert">
          <p>Não foi possível carregar os temas.</p>
          {onRetry && (
            <button type="button" onClick={onRetry}>
              Tentar novamente
            </button>
          )}
        </div>
      </section>
    );
  }

  if (topics.length === 0) return null;

  return (
    <section className="content-topics" aria-labelledby="content-topics-title">
      <div className="content-topics__header">
        <div className="content-topics__heading">
          <h1 id="content-topics-title">Temas</h1>
          {topics.length > 0 && (
            <button
              type="button"
              className="content-topics__mobile-trigger"
              onClick={() => setIsTopicsDialogOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={isTopicsDialogOpen}
            >
              <span>{selectedTopic?.name ?? "Escolher tema"}</span>
              <ChevronRightRoundedIcon aria-hidden="true" />
            </button>
          )}
        </div>

        <div className="content-topics__actions">
          {selectedTopic && (
            <button
              type="button"
              className="content-topics__clear"
              onClick={() => onTopicChange(null)}
            >
              Limpar tema
            </button>
          )}

          {(showUploadFilter || showTagFilter) && (
            <button
              type="button"
              className={`content-topics__filter-button${
                selectedUploadWindow || selectedTag
                  ? " content-topics__filter-button--active"
                  : ""
              }`}
              onClick={(event) =>
                setFilterAnchor((anchor) =>
                  anchor ? null : event.currentTarget
                )
              }
              aria-controls={isFilterOpen ? "content-upload-filter" : undefined}
              aria-expanded={isFilterOpen}
              aria-haspopup="dialog"
            >
              <span>Filtros</span>
              <TuneRoundedIcon aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      {topics.length > 0 && (
        <div
          className={`content-topics__track${
            isDragging ? " content-topics__track--dragging" : ""
          }`}
          ref={trackRef}
          {...dragProps}
        >
          {topics.map((topic) => (
            <button
              type="button"
              className={`content-topics__item${
                selectedTopicId === topic.id
                  ? " content-topics__item--active"
                  : ""
              }`}
              key={topic.id}
              onClick={() => toggleTopic(topic.id)}
              aria-pressed={selectedTopicId === topic.id}
            >
              <span>{topic.name}</span>
              <ChevronRightRoundedIcon aria-hidden="true" />
            </button>
          ))}
        </div>
      )}

      <Dialog
        open={isTopicsDialogOpen}
        onClose={() => setIsTopicsDialogOpen(false)}
        container={() => document.querySelector<HTMLElement>(".app-shell")}
        fullWidth
        maxWidth="xs"
        PaperProps={{ className: "content-topics-dialog" }}
        BackdropProps={{ className: "content-topics-dialog__backdrop" }}
        aria-labelledby="content-topics-dialog-title"
      >
        <div className="content-topics-dialog__content">
          <div className="content-topics-dialog__heading">
            <div>
              <span>Conteúdo</span>
              <h2 id="content-topics-dialog-title">Escolha um tema</h2>
            </div>
            <button
              type="button"
              className="content-topics-dialog__close"
              onClick={() => setIsTopicsDialogOpen(false)}
              aria-label="Fechar temas"
            >
              <CloseRoundedIcon aria-hidden="true" />
            </button>
          </div>

          <div className="content-topics-dialog__options">
            <button
              type="button"
              className={`content-topics-dialog__option${
                selectedTopicId === null
                  ? " content-topics-dialog__option--selected"
                  : ""
              }`}
              onClick={() => selectTopic(null)}
              aria-pressed={selectedTopicId === null}
            >
              <span>{ALL_TOPICS_LABEL}</span>
              <ChevronRightRoundedIcon aria-hidden="true" />
            </button>

            {topics.map((topic) => (
              <button
                type="button"
                className={`content-topics-dialog__option${
                  selectedTopicId === topic.id
                    ? " content-topics-dialog__option--selected"
                    : ""
                }`}
                key={topic.id}
                onClick={() => selectTopic(topic.id)}
                aria-pressed={selectedTopicId === topic.id}
              >
                <span>{topic.name}</span>
                <ChevronRightRoundedIcon aria-hidden="true" />
              </button>
            ))}
          </div>
        </div>
      </Dialog>

      <Popover
        id="content-upload-filter"
        open={isFilterOpen}
        anchorEl={filterAnchor}
        onClose={() => setFilterAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{ className: "content-filter-dialog", elevation: 0 }}
      >
        <div
          className="content-filter-dialog__content"
          role="dialog"
          aria-label={showTagFilter ? "Filtros por tag" : "Filtros por data do upload"}
        >
          <h2>{showTagFilter ? "Tags" : "Data do upload"}</h2>
          <div className="content-filter-dialog__options">
            {showTagFilter ? (
              <>
                <button
                  type="button"
                  className={`content-filter-dialog__option${
                    selectedTag === null
                      ? " content-filter-dialog__option--selected"
                      : ""
                  }`}
                  onClick={() => selectTag(null)}
                  aria-pressed={selectedTag === null}
                >
                  Todas as tags
                </button>
                {availableTags.map((tag) => (
                  <button
                    type="button"
                    key={tag}
                    className={`content-filter-dialog__option${
                      selectedTag === tag
                        ? " content-filter-dialog__option--selected"
                        : ""
                    }`}
                    onClick={() => selectTag(tag)}
                    aria-pressed={selectedTag === tag}
                  >
                    #{tag}
                  </button>
                ))}
              </>
            ) : (
              <>
                <button
                  type="button"
                  className={`content-filter-dialog__option${
                    selectedUploadWindow === null
                      ? " content-filter-dialog__option--selected"
                      : ""
                  }`}
                  onClick={() => selectUploadWindow(null)}
                  aria-pressed={selectedUploadWindow === null}
                >
                  Qualquer data
                </button>
                {UPLOAD_WINDOW_OPTIONS.map((option) => (
                  <button
                    type="button"
                    key={option.value}
                    className={`content-filter-dialog__option${
                      selectedUploadWindow === option.value
                        ? " content-filter-dialog__option--selected"
                        : ""
                    }`}
                    onClick={() => selectUploadWindow(option.value)}
                    aria-pressed={selectedUploadWindow === option.value}
                  >
                    {option.label}
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      </Popover>
    </section>
  );
};

export default React.memo(ContentTopics);
